import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { motion, AnimatePresence } from "framer-motion";
import { RotateCcw, Star, Timer } from "lucide-react";
import { type Difficulty } from "./difficulty";
import { useSpeech } from "@/hooks/useSpeech";

const ALL_PAIRS = ["🐱", "🐶", "🐸", "🦋", "🌸", "🍎", "⭐", "🌙", "🐠", "🦊", "🐻", "🐼"];

type CardData = { id: number; emoji: string; flipped: boolean; matched: boolean };

const createBoard = (pairCount: number = 6): CardData[] => {
  const selected = ALL_PAIRS.slice(0, pairCount);
  const pairs = [...selected, ...selected];
  return pairs
    .sort(() => Math.random() - 0.5)
    .map((emoji, i) => ({ id: i, emoji, flipped: false, matched: false }));
};

interface Props {
  onComplete?: (result: { score: number; maxScore: number; stars: number }) => void;
  difficulty?: Difficulty;
}

export default function MemoryGame({ onComplete, difficulty = "medium" }: Props) {
  const pairCount = difficulty === "easy" ? 4 : difficulty === "hard" ? 8 : 6;
  const [cards, setCards] = useState<CardData[]>(() => createBoard(pairCount));
  const [flippedIds, setFlippedIds] = useState<number[]>([]);
  const [moves, setMoves] = useState(0);
  const [matchCount, setMatchCount] = useState(0);
  const [isChecking, setIsChecking] = useState(false);
  const [gameComplete, setGameComplete] = useState(false);
  const [timer, setTimer] = useState(0);
  const [reported, setReported] = useState(false);
  const { encourageCorrect, encourageWrong } = useSpeech();

  useEffect(() => {
    if (gameComplete) return;
    const interval = setInterval(() => setTimer((t) => t + 1), 1000);
    return () => clearInterval(interval);
  }, [gameComplete]);

  const handleFlip = (id: number) => {
    if (isChecking || flippedIds.length >= 2) return;
    const card = cards.find((c) => c.id === id);
    if (!card || card.flipped || card.matched) return;

    const updated = cards.map((c) => (c.id === id ? { ...c, flipped: true } : c));
    setCards(updated);
    const newFlipped = [...flippedIds, id];
    setFlippedIds(newFlipped);

    if (newFlipped.length === 2) {
      setMoves((m) => m + 1);
      setIsChecking(true);
      const [first, second] = newFlipped.map((fid) => updated.find((c) => c.id === fid)!);

      if (first.emoji === second.emoji) {
        setTimeout(() => {
          setCards((prev) =>
            prev.map((c) => (c.id === first.id || c.id === second.id ? { ...c, matched: true } : c))
          );
          setFlippedIds([]);
          setIsChecking(false);
          encourageCorrect();
          setMatchCount((m) => {
            const newCount = m + 1;
            if (newCount >= pairCount) setGameComplete(true);
            return newCount;
          });
        }, 500);
      } else {
        setTimeout(() => {
          setCards((prev) =>
            prev.map((c) => (c.id === first.id || c.id === second.id ? { ...c, flipped: false } : c))
          );
          setFlippedIds([]);
          setIsChecking(false);
        }, 800);
      }
    }
  };

  const reset = () => {
    setCards(createBoard());
    setFlippedIds([]);
    setMoves(0);
    setMatchCount(0);
    setIsChecking(false);
    setGameComplete(false);
    setTimer(0);
    setReported(false);
  };

  const stars = moves <= 8 ? 5 : moves <= 12 ? 4 : moves <= 16 ? 3 : moves <= 20 ? 2 : 1;

  useEffect(() => {
    if (gameComplete && !reported) {
      setReported(true);
      // Memory game: score based on fewer moves = better
      const maxScore = 60; // 6 pairs * 10
      const score = Math.max(10, maxScore - (moves - 6) * 3);
      onComplete?.({ score, maxScore, stars });
    }
  }, [gameComplete]);

  if (gameComplete) {
    return (
      <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
        <Card className="max-w-md mx-auto text-center shadow-elevated">
          <CardContent className="p-8 space-y-6">
            <motion.div animate={{ scale: [1, 1.2, 1] }} transition={{ repeat: Infinity, duration: 1.5 }} className="text-6xl">
              🧠
            </motion.div>
            <h2 className="text-2xl font-bold text-foreground">رائع! 🎊</h2>
            <p className="text-muted-foreground">أكملت اللعبة في {moves} محاولة و {timer} ثانية</p>
            <div className="flex justify-center gap-1">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} className={`w-8 h-8 ${i < stars ? "text-yellow-400 fill-yellow-400" : "text-muted"}`} />
              ))}
            </div>
            <Button onClick={reset} size="lg" className="gradient-hero border-0 text-primary-foreground">
              <RotateCcw className="w-4 h-4 ml-2" />
              اللعب مرة أخرى
            </Button>
          </CardContent>
        </Card>
      </motion.div>
    );
  }

  return (
    <div className="max-w-md mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <Badge variant="secondary" className="text-sm px-3 py-1">🎯 {moves} محاولة</Badge>
        <Badge variant="outline" className="text-sm px-3 py-1 flex items-center gap-1">
          <Timer className="w-3 h-3" /> {timer} ث
        </Badge>
        <Badge variant="secondary" className="text-sm px-3 py-1">✅ {matchCount}/6</Badge>
      </div>

      <div className="grid grid-cols-3 gap-3">
        {cards.map((card) => (
          <motion.div
            key={card.id}
            whileHover={!card.flipped && !card.matched ? { scale: 1.05 } : {}}
            whileTap={!card.flipped && !card.matched ? { scale: 0.95 } : {}}
          >
            <div
              className="relative aspect-square cursor-pointer"
              onClick={() => handleFlip(card.id)}
              style={{ perspective: 600 }}
            >
              <motion.div
                className="w-full h-full"
                animate={{ rotateY: card.flipped || card.matched ? 180 : 0 }}
                transition={{ duration: 0.4, type: "spring" }}
                style={{ transformStyle: "preserve-3d" }}
              >
                {/* Back (hidden) */}
                <div
                  className="absolute inset-0 rounded-xl bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center shadow-card"
                  style={{ backfaceVisibility: "hidden" }}
                >
                  <span className="text-3xl text-white font-bold">؟</span>
                </div>
                {/* Front (emoji) */}
                <div
                  className={`absolute inset-0 rounded-xl flex items-center justify-center shadow-card ${
                    card.matched ? "bg-emerald-100 dark:bg-emerald-900/30 border-2 border-emerald-400" : "bg-card border-2 border-border"
                  }`}
                  style={{ backfaceVisibility: "hidden", transform: "rotateY(180deg)" }}
                >
                  <motion.span
                    className="text-4xl"
                    animate={card.matched ? { scale: [1, 1.3, 1] } : {}}
                    transition={{ duration: 0.5 }}
                  >
                    {card.emoji}
                  </motion.span>
                </div>
              </motion.div>
            </div>
          </motion.div>
        ))}
      </div>

      <Button variant="outline" onClick={reset} className="w-full">
        <RotateCcw className="w-4 h-4 ml-2" />
        إعادة اللعب
      </Button>
    </div>
  );
}
